/**
 * Axios Instance Configuration for Claims Portal BFF Integration
 *
 * This configures axios with:
 * - Base URL from environment variables
 * - JWT token injection from parent app context (Authorization header)
 * - Tenant ID injection from parent app context (X-Tenant-ID header - MANDATORY)
 * - Request/response interceptors
 * - Automatic retry logic with exponential backoff
 * - Error transformation for user-friendly messages
 *
 * CRITICAL Headers:
 * - Authorization: Bearer <JWT> (authentication)
 * - X-Tenant-ID: <tenantId> (multi-tenant isolation - REQUIRED)
 *
 * Both JWT and Tenant ID are extracted from:
 * - window.parentContext (primary source)
 * - window.embeddedAppContext (fallback)
 */

import axios, { AxiosError } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import type { ApiErrorResponse } from '../../types/api';
import { transformError, shouldRetry, calculateBackoff, isWAFError } from '../../utils/errorTransformer';
import { logger } from '../../utils/logger';
import { API_TIMEOUTS } from '../../config/apiTimeouts';
import { useUIStore } from '../../store/uiStore';
import { getSessionId } from '../../utils/sessionManager';

/**
 * Retry configuration
 */
interface RetryConfig {
  retryCount?: number;
  errorShown?: boolean; // Track if we already showed notification for this error
  skipAuth?: boolean; // When true, do NOT inject the Authorization header (public endpoints, e.g. the maintenance probe)
}

/**
 * Augment axios's request config with `suppressGlobalErrorToast`. Callers set
 * this to `true` when they handle the error inline in their own UI (e.g. an
 * Alert on a specific screen), so the global error toast in the response
 * interceptor stays quiet. Status propagation to the caller is unaffected.
 */
declare module 'axios' {
  interface AxiosRequestConfig {
    suppressGlobalErrorToast?: boolean;
    /**
     * BFF business codes (error body `codes`) the CALLER handles itself (DEV-8836). When the
     * error carries one of them the global toast stays quiet; every other error on the same
     * call keeps its toast. Narrower than {@link suppressGlobalErrorToast}.
     */
    quietBusinessCodes?: readonly string[];
  }
}

const isHandledByCaller = (
  transformed: { businessCodes?: string[] },
  quietBusinessCodes: readonly string[] | undefined
): boolean =>
  !!quietBusinessCodes?.length &&
  !!transformed.businessCodes?.some((code) => quietBusinessCodes.includes(code));

/**
 * Remove sensitive request data from an axios error before it escapes to callers:
 * the Authorization JWT (its claims carry the client's identity) and the request
 * body (names, contract numbers, banking details). Stripping at the boundary
 * guarantees no downstream logging of a caught error can ship PII to Datadog.
 * Retries are unaffected because stripping only happens on terminal rejection
 * paths — never before a `return axiosInstance(config)`. Do not resubmit a
 * stripped config: the token would be re-injected by the request interceptor,
 * but the deleted request body would not come back.
 */
function stripSensitiveConfig<T>(error: T): T {
  const config = (error as any)?.config;
  if (!config) {
    return error;
  }
  const headers = config.headers;
  if (headers) {
    if (typeof headers.delete === 'function') {
      headers.delete('Authorization'); // AxiosHeaders (case-insensitive)
    } else {
      delete headers.Authorization;
      delete headers.authorization;
    }
  }
  delete config.data;
  return error;
}

/**
 * Show error notification
 * Gets current language and shows bilingual error message
 */
function showErrorNotification(transformed: any): void {
  try {
    const state = useUIStore.getState();
    const language = state.language?.toLowerCase() || 'fr';
    const lang = language.startsWith('en') ? 'en' : 'fr';

    const title = transformed.title?.[lang] || transformed.title?.fr;
    const message = transformed.message?.[lang] || transformed.message?.fr;

    // Always show as error (persistent) - user needs to be aware even if retryable
    // Retries happen automatically in background, but error should remain visible
    state.showError(message, title);
  } catch (err) {
    logger.error('Failed to show error notification', {}, err as Error);
  }
}

/**
 * Base axios instance for BFF API calls
 *
 * Development (local):
 * - VITE_API_BASE_URL = http://localhost:5000 (backend running locally)
 *
 * Cloud deployment:
 * - VITE_API_BASE_URL = '' (empty/undefined)
 * - Requests use relative paths (e.g., /api/v1/claims)
 * - nginx reverse proxy forwards /api/* to backend service (claims-portal-edge-api)
 */
const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  timeout: API_TIMEOUTS.default,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

/**
 * Request interceptor to inject JWT token and handle retry metadata
 */
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig & RetryConfig) => {
    // Initialize retry count
    config.retryCount = config.retryCount || 0;

    if (!config.timeout) {
      config.timeout = API_TIMEOUTS.default;
    }

    // Check if Authorization header is already present (e.g., manually set by caller)
    const hasAuthHeader = !!config.headers.Authorization;

    // Public endpoints (skipAuth) must NOT carry a Bearer token: the maintenance probe is public and
    // tenant-aware via X-Tenant-ID only. Sending a JWT to it makes the resource server attempt (and
    // fail) to validate the token on a path with no tenant context, yielding a spurious 401 (DEV-6343).
    if (!config.skipAuth && !hasAuthHeader) {
      // Inject JWT token from parent app context (both DEV and PRODUCTION)
      // Priority: 1. window.parentContext.jwt, 2. window.embeddedAppContext.token
      const jwt = (window as any).parentContext?.jwt ||
                  (window as any).embeddedAppContext?.token;

      if (jwt) {
        config.headers.Authorization = `Bearer ${jwt}`;
      } else {
        logger.warn('No JWT token found - API calls may fail. Make sure context is loaded.');
      }
    }

    // CRITICAL: Inject Tenant ID from parent app context (MANDATORY for multi-tenant backend)
    // Priority: 1. window.parentContext.tenantId, 2. window.embeddedAppContext.tenantId
    const tenantId = (window as any).parentContext?.tenantId ||
                     (window as any).embeddedAppContext?.tenantId;

    if (tenantId) {
      config.headers['X-Tenant-ID'] = tenantId;
    } else {
      logger.warn('No tenant ID found - API calls will fail. Make sure context includes tenantId.');
    }

    // Add claim ID header if available
    const claimId = (window as any).__CLAIM_ID__;
    if (claimId) {
      config.headers['X-Claim-ID'] = claimId;
    }

    // Session ID for end-to-end user journey tracing (DEV-6594)
    config.headers['X-Session-ID'] = getSessionId();

    return config;
  },
  (error) => {
    // Client-side request configuration error (rare)
    logger.error('Request configuration error', {
      errorType: 'RequestConfigError'
    }, error);
    return Promise.reject(stripSensitiveConfig(error));
  }
);

/**
 * Response interceptor with automatic retry logic and error transformation
 */
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // Success - no logging needed for successful responses
    return response;
  },
  async (error: AxiosError<ApiErrorResponse>) => {
    const config = error.config as (InternalAxiosRequestConfig & RetryConfig) | undefined;

    if (!config) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const retryCount = config.retryCount || 0;
    const errorShown = config.errorShown || false;

    // Maintenance mode (DEV-6343): a 503 carrying the X-Maintenance header means the portal is in
    // maintenance for this tenant. Switch to the maintenance page immediately (no retry, no error
    // notification).
    const maintenanceHeader = error.response?.headers?.['x-maintenance'];
    if (status === 503 && String(maintenanceHeader ?? '').toLowerCase() === 'true') {
      try {
        useUIStore.getState().setMaintenance(true);
      } catch (maintenanceErr) {
        logger.error('Failed to enter maintenance mode', {}, maintenanceErr as Error);
      }
      return Promise.reject(stripSensitiveConfig(error));
    }

    // Check for WAF errors - MUST LOG
    const wafCheck = isWAFError(error);
    if (wafCheck.isWAF) {
      logger.error('WAF block detected', {
        errorType: 'WAF_BLOCK',
        wafTrackingId: wafCheck.trackingId,
        httpStatus: 403,
      }, error);
    }

    // Check for network errors - MUST LOG
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      logger.error('Network error', {
        errorType: error.code,
      }, error);
    }

    // Backend errors (400, 404, 500) are NOT logged - handled by logger.isClientSideError()

    // Transform error immediately for notification.
    //
    // No `supportContact` is passed, and that is deliberate (DEV-7932): this interceptor runs
    // outside React, so `useEmbeddedApp()` — the only sanctioned way to reach the tenant config —
    // is unavailable here, and reading the flag from a module-scope cache or a `window` global
    // would put a second reader of `features.displaySupportContact` next to
    // `utils/supportContact`, the one thing DEV-7934 needs to stay single. The accepted
    // consequence: an error surfaced ONLY through this notification carries no assistance line,
    // even for the insurer entitled to publish one. It fails closed — a missing number costs a
    // support call, whereas the previously hardcoded one sent other insurers' members to a
    // claims department that cannot open their file. Screens that display a transformed error
    // themselves pass the resolved contact (see `screens/BankingInfoScreen`).
    const transformed = transformError(error, {
      url: config.url,
      method: config.method,
    });

    // Check if we should retry (silently, notification shown only after all retries are exhausted)
    if (shouldRetry(error, retryCount)) {
      config.retryCount = retryCount + 1;
      config.errorShown = true; // Ensure flag is preserved for retry
      const backoffDelay = calculateBackoff(config.retryCount);

      // Handle rate limiting - respect Retry-After header
      if (status === 429) {
        const retryAfter = error.response?.headers?.['retry-after'];
        if (retryAfter) {
          const delayMs = parseInt(retryAfter, 10) * 1000;
          await new Promise((resolve) => setTimeout(resolve, delayMs));
          return axiosInstance(config);
        }
      }

      // Wait for backoff delay, then retry
      await new Promise((resolve) => setTimeout(resolve, backoffDelay));
      return axiosInstance(config);
    }

    // All retries exhausted - show global notification UNLESS the caller opted out
    // (handling the error inline in its own UI).
    if (!errorShown && !config.suppressGlobalErrorToast
        && !isHandledByCaller(transformed, config.quietBusinessCodes)) {
      showErrorNotification(transformed);
    }

    // Handle specific error cases
    if (status === 401) {
      logger.warn('Unauthorized - JWT token may be invalid or expired');
      // Notify parent app to refresh token
      const parentOrigin = (window as any).parentContext?.origin ||
                          import.meta.env.VITE_PARENT_APP_ORIGIN ||
                          '*';

      try {
        window.parent.postMessage(
          { type: 'TOKEN_EXPIRED', payload: { returnUrl: window.location.pathname } },
          parentOrigin
        );
      } catch (error) {
        logger.error('Failed to send TOKEN_EXPIRED message to parent', {}, error as Error);
      }
    }

    // Add metadata for tracking
    (transformed as any).__retryCount = retryCount;

    // Reject with transformed error (notification shown after retries).
    // The raw error is stripped (not the transformed one, which has no config to
    // strip): it is the only object holding the sensitive config, and stripping it
    // covers any reference to it that survives elsewhere.
    stripSensitiveConfig(error);
    return Promise.reject(transformed);
  }
);

/**
 * Set JWT token manually (for testing or manual context injection)
 */
export function setJwtToken(token: string): void {
  (window as any).parentContext = {
    ...(window as any).parentContext,
    jwt: token,
  };
}

/**
 * Clear JWT token
 */
export function clearJwtToken(): void {
  if ((window as any).parentContext) {
    delete (window as any).parentContext.jwt;
  }
}

export default axiosInstance;
